// src/components/admin/HomeConfigEditor.js
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { database } from "../../firebaseConfig";
import { ref, set, get } from "firebase/database";
import "./HomeConfigEditor.css";
import { config } from '../../config/config'
const API_URL = config.baseUrl;

const HomeConfigEditor = () => {
    // State for various configuration elements
    const [videoUrl, setVideoUrl] = useState("");
    const [phoneVideoUrl, setPhoneVideoUrl] = useState(""); // For phone view hero video
    const [whyUsVideoUrl, setWhyUsVideoUrl] = useState("");
    const [promoVideoUrl, setPromoVideoUrl] = useState("");
    const [whyUsContent, setWhyUsContent] = useState([]);
    const [topProducts, setTopProducts] = useState(["", "", ""]); // Array for 3 SKUs
    const [bodyBackgroundColor, setBodyBackgroundColor] = useState("");
    const [sectionConfig, setSectionConfig] = useState({});
    // New state for SEO Metadata
    const [metaDescription, setMetaDescription] = useState("");
    const [metaKeywords, setMetaKeywords] = useState("");

    const promoVideoInputRef = useRef(null);

    // Fetch current home config from Firebase on mount
    useEffect(() => {
        const fetchHomeConfig = async () => {
            try {
                const snapshot = await get(ref(database, "homeconfig"));
                if (snapshot.exists()) {
                    const data = snapshot.val();
                    setVideoUrl(data.videoUrl || "");
                    setPhoneVideoUrl(data.phoneVideoUrl || "");
                    setWhyUsVideoUrl(data.whyUsVideoUrl || "");
                    setPromoVideoUrl(data.promoVideoUrl || "");
                    setWhyUsContent(data.whyUsContent || []);
                    setTopProducts(data.topProducts || ["", "", ""]);
                    setBodyBackgroundColor(data.bodyBackgroundColor || "");
                    setSectionConfig(data.sectionConfig || {});
                    // Load SEO metadata if available
                    setMetaDescription(data.seo?.metaDescription || "");
                    setMetaKeywords(data.seo?.metaKeywords || "");
                }
            } catch (error) {
                console.error("Error fetching home config:", error);
            }
        };
        fetchHomeConfig();
    }, []);

    // Upload helper for video or image file
    const uploadFile = async (file) => {
        if (!file) return "";
        const formData = new FormData();
        formData.append("media", file);
        try {
            const response = await axios.post(`${API_URL}upload`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return response.data.fileUrl || "";
        } catch (error) {
            console.error("Error uploading file:", error);
            return "";
        }
    };

    // Delete a file using API
    const deleteFile = async (fileUrl) => {
        if (!fileUrl) return;
        const parts = fileUrl.split("/");
        const filename = parts[parts.length - 1];
        try {
            await axios.delete(`${API_URL}delete`, { data: { filename } });
            console.log("Old file deleted successfully");
        } catch (error) {
            console.error("Error deleting file:", error);
        }
    };

    // Handle Hero Video change (Desktop)
    const handleVideoFileChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            if (videoUrl) {
                await deleteFile(videoUrl);
            }
            const newUrl = await uploadFile(file);
            setVideoUrl(newUrl);
        }
    };

    // Handle Hero Phone Video change (for phone view)
    const handlePhoneVideoFileChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            if (phoneVideoUrl) {
                await deleteFile(phoneVideoUrl);
            }
            const newUrl = await uploadFile(file);
            setPhoneVideoUrl(newUrl);
        }
    };

    // Handle Why Us Video change
    const handleWhyUsVideoFileChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            if (whyUsVideoUrl) {
                await deleteFile(whyUsVideoUrl);
            }
            const newUrl = await uploadFile(file);
            setWhyUsVideoUrl(newUrl);
        }
    };

    // Handle Promo Video change
    const handlePromoVideoFileChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            if (promoVideoUrl) {
                await deleteFile(promoVideoUrl);
            }
            const newUrl = await uploadFile(file);
            setPromoVideoUrl(newUrl);
        }
    };

    // Handle image file change for a specific whyUsContent item
    const handleImageFileChange = async (index, e) => {
        const file = e.target.files[0];
        if (file) {
            if (whyUsContent[index].categoryUrl) {
                await deleteFile(whyUsContent[index].categoryUrl);
            }
            const newUrl = await uploadFile(file);
            const updatedContent = [...whyUsContent];
            updatedContent[index].categoryUrl = newUrl;
            setWhyUsContent(updatedContent);
        }
    };

    // Handle change for top products SKUs
    const handleTopProductChange = (index, value) => {
        const updated = [...topProducts];
        updated[index] = value;
        setTopProducts(updated);
    };

    // Handle change for section configuration fields
    const handleSectionConfigChange = (sectionKey, field, value) => {
        setSectionConfig((prev) => ({
            ...prev,
            [sectionKey]: {
                ...prev[sectionKey],
                [field]: field === "order" ? parseInt(value) : value,
            },
        }));
    };
    const addTopProduct = () => {
        setTopProducts([...topProducts, ""]);
    };

    const removeTopProduct = (index) => {
        const updated = topProducts.filter((_, i) => i !== index);
        setTopProducts(updated);
    };
    // Save home config to Firebase (including SEO metadata)
    const handleSaveConfig = async () => {
        const homeConfig = {
            videoUrl,
            phoneVideoUrl,
            whyUsVideoUrl,
            promoVideoUrl,
            whyUsContent,
            topProducts,
            bodyBackgroundColor,
            sectionConfig,
            seo: {
                metaDescription,
                metaKeywords,
            },
        };

        try {
            await set(ref(database, "homeconfig"), homeConfig);
            alert("Home configuration saved successfully!");
        } catch (error) {
            console.error("Error saving home config:", error);
            alert("Error saving configuration.");
        }
    };

    return (
        <div className="HomeConfigEditor">
            <h2 className="HomeConfigEditor-title">Edit Home Config</h2>

            {/* Body Background Color */}
            <div className="HomeConfigEditor-section">
                <label className="HomeConfigEditor-label">
                    Body Background Color:
                </label>
                <input
                    type="color"
                    className="HomeConfigEditor-input"
                    value={bodyBackgroundColor}
                    onChange={(e) => setBodyBackgroundColor(e.target.value)}
                />
            </div>

            {/* SEO Metadata Section */}
            <div className="HomeConfigEditor-section">
                <h3 className="HomeConfigEditor-subtitle">SEO Metadata</h3>
                <label className="HomeConfigEditor-label">
                    Meta Description:
                </label>
                <textarea
                    className="HomeConfigEditor-textarea"
                    value={metaDescription}
                    onChange={(e) => setMetaDescription(e.target.value)}
                    placeholder="Enter meta description..."
                />
                <label className="HomeConfigEditor-label">
                    Meta Keywords:
                </label>
                <input
                    type="text"
                    className="HomeConfigEditor-textarea"
                    value={metaKeywords}
                    onChange={(e) => setMetaKeywords(e.target.value)}
                    placeholder="Enter comma separated keywords..."
                />
            </div>

            {/* Hero Video Section */}
            <div className="HomeConfigEditor-section">
                <label className="HomeConfigEditor-label">Hero Video (Desktop):</label>
                <div className="HomeConfigEditor-video-container">
                    {videoUrl && (
                        <div className="HomeConfigEditor-video-preview">
                            <video width="320" height="180" controls>
                                <source src={videoUrl} type="video/mp4" />
                            </video>
                            <button
                                className="HomeConfigEditor-button HomeConfigEditor-delete-button"
                                onClick={() => {
                                    deleteFile(videoUrl);
                                    setVideoUrl("");
                                }}
                            >
                                Delete Video
                            </button>
                        </div>
                    )}
                    <input
                        type="file"
                        accept="video/*"
                        className="HomeConfigEditor-input-file"
                        onChange={handleVideoFileChange}
                    />
                </div>
                {/* Subsection for hero section styling */}
                <div className="HomeConfigEditor-subsection">
                    <label className="HomeConfigEditor-label">
                        Hero Section Background Color:
                    </label>
                    <input
                        type="color"
                        className="HomeConfigEditor-input"
                        value={sectionConfig.hero?.backgroundColor || ""}
                        onChange={(e) =>
                            handleSectionConfigChange("hero", "backgroundColor", e.target.value)
                        }
                    />
                    <label className="HomeConfigEditor-label">Order:</label>
                    <input
                        type="number"
                        className="HomeConfigEditor-input"
                        value={sectionConfig.hero?.order || ""}
                        onChange={(e) =>
                            handleSectionConfigChange("hero", "order", e.target.value)
                        }
                        min="1"
                    />
                </div>

                {/* NEW: Hero Phone Video Section */}
                <div className="HomeConfigEditor-section">
                    <label className="HomeConfigEditor-label">Hero Video (Phone View):</label>
                    <div className="HomeConfigEditor-video-container">
                        {phoneVideoUrl && (
                            <div className="HomeConfigEditor-video-preview">
                                <video width="320" height="180" controls>
                                    <source src={phoneVideoUrl} type="video/mp4" />
                                </video>
                                <button
                                    className="HomeConfigEditor-button HomeConfigEditor-delete-button"
                                    onClick={() => {
                                        deleteFile(phoneVideoUrl);
                                        setPhoneVideoUrl("");
                                    }}
                                >
                                    Delete Phone Video
                                </button>
                            </div>
                        )}
                        <input
                            type="file"
                            accept="video/*"
                            className="HomeConfigEditor-input-file"
                            onChange={handlePhoneVideoFileChange}
                        />
                    </div>
                </div>
            </div>

            {/* Why Us Video Section */}
            <div className="HomeConfigEditor-section">
                <label className="HomeConfigEditor-label">Why Us Video:</label>
                <div className="HomeConfigEditor-video-container">
                    {whyUsVideoUrl && (
                        <div className="HomeConfigEditor-video-preview">
                            <video width="320" height="180" controls>
                                <source src={whyUsVideoUrl} type="video/mp4" />
                            </video>
                            <button
                                className="HomeConfigEditor-button HomeConfigEditor-delete-button"
                                onClick={() => {
                                    deleteFile(whyUsVideoUrl);
                                    setWhyUsVideoUrl("");
                                }}
                            >
                                Delete Video
                            </button>
                        </div>
                    )}
                    <input
                        type="file"
                        accept="video/*"
                        className="HomeConfigEditor-input-file"
                        onChange={handleWhyUsVideoFileChange}
                    />
                </div>
                <div className="HomeConfigEditor-subsection">
                    <label className="HomeConfigEditor-label">
                        Why Choose Us Section Background Color:
                    </label>
                    <input
                        type="color"
                        className="HomeConfigEditor-input"
                        value={sectionConfig.whyChooseUs?.backgroundColor || ""}
                        onChange={(e) =>
                            handleSectionConfigChange("whyChooseUs", "backgroundColor", e.target.value)
                        }
                    />
                    <label className="HomeConfigEditor-label">Order:</label>
                    <input
                        type="number"
                        className="HomeConfigEditor-input"
                        value={sectionConfig.whyChooseUs?.order || ""}
                        onChange={(e) =>
                            handleSectionConfigChange("whyChooseUs", "order", e.target.value)
                        }
                        min="1"
                    />
                </div>
            </div>

            {/* Promotional Video Section */}
            <div className="HomeConfigEditor-section">
                <label className="HomeConfigEditor-label">Promotional Video:</label>
                <div className="HomeConfigEditor-video-container">
                    {promoVideoUrl && (
                        <div className="HomeConfigEditor-video-preview">
                            <video width="320" height="180" controls>
                                <source src={promoVideoUrl} type="video/mp4" />
                            </video>
                            <button
                                className="HomeConfigEditor-button HomeConfigEditor-delete-button"
                                onClick={() => {
                                    deleteFile(promoVideoUrl);
                                    setPromoVideoUrl("");
                                }}
                            >
                                Delete Video
                            </button>
                        </div>
                    )}
                    <input
                        type="file"
                        accept="video/*"
                        className="HomeConfigEditor-input-file"
                        ref={promoVideoInputRef}
                        onChange={handlePromoVideoFileChange}
                    />
                </div>
                <div className="HomeConfigEditor-subsection">
                    <label className="HomeConfigEditor-label">
                        Promo Video Section Background Color:
                    </label>
                    <input
                        type="color"
                        className="HomeConfigEditor-input"
                        value={sectionConfig.promoVideo?.backgroundColor || ""}
                        onChange={(e) =>
                            handleSectionConfigChange("promoVideo", "backgroundColor", e.target.value)
                        }
                    />
                    <label className="HomeConfigEditor-label">Order:</label>
                    <input
                        type="number"
                        className="HomeConfigEditor-input"
                        value={sectionConfig.promoVideo?.order || ""}
                        onChange={(e) =>
                            handleSectionConfigChange("promoVideo", "order", e.target.value)
                        }
                        min="1"
                    />
                </div>
            </div>

            {/* Top Products Section */}
            <div className="HomeConfigEditor-section">
                <h3 className="HomeConfigEditor-subtitle">Top Products SKUs:</h3>

                {topProducts.map((sku, index) => (
                    <div key={index} className="HomeConfigEditor-top-product-input">
                        <label className="HomeConfigEditor-label">
                            Product {index + 1} SKU:
                        </label>
                        <input
                            type="text"
                            className="HomeConfigEditor-input"
                            value={sku}
                            onChange={(e) => handleTopProductChange(index, e.target.value)}
                            placeholder="Enter SKU"
                        />
                        <button
                            type="button"
                            onClick={() => removeTopProduct(index)}
                            className="HomeConfigEditor-remove-button"
                            style={{ marginLeft: "8px", color: "red" }}
                        >
                            ✕
                        </button>
                    </div>
                ))}

                <button
                    type="button"
                    onClick={addTopProduct}
                    className="HomeConfigEditor-add-button"
                    style={{ marginTop: "10px" }}
                >
                    ➕ Add SKU
                </button>

                <div className="HomeConfigEditor-subsection" style={{ marginTop: "20px" }}>
                    <label className="HomeConfigEditor-label">
                        Top Products Section Background Color:
                    </label>
                    <input
                        type="color"
                        className="HomeConfigEditor-input"
                        value={sectionConfig.topProducts?.backgroundColor || ""}
                        onChange={(e) =>
                            handleSectionConfigChange("topProducts", "backgroundColor", e.target.value)
                        }
                    />
                    <label className="HomeConfigEditor-label">Order:</label>
                    <input
                        type="number"
                        className="HomeConfigEditor-input"
                        value={sectionConfig.topProducts?.order || ""}
                        onChange={(e) =>
                            handleSectionConfigChange("topProducts", "order", e.target.value)
                        }
                        min="1"
                    />
                </div>
            </div>


            {/* Why Us Content Section */}
            <div className="HomeConfigEditor-section">
                <h3 className="HomeConfigEditor-subtitle">Why Us Content:</h3>
                {whyUsContent.map((content, index) => (
                    <div key={index} className="HomeConfigEditor-content-item">
                        <div className="HomeConfigEditor-content-row">
                            <label className="HomeConfigEditor-label">Category Title:</label>
                            <input
                                type="text"
                                className="HomeConfigEditor-input"
                                value={content.category}
                                onChange={(e) => {
                                    const updatedContent = [...whyUsContent];
                                    updatedContent[index].category = e.target.value;
                                    setWhyUsContent(updatedContent);
                                }}
                            />
                        </div>
                        <div className="HomeConfigEditor-content-row">
                            <label className="HomeConfigEditor-label">Description:</label>
                            <textarea
                                className="HomeConfigEditor-textarea"
                                value={content.description}
                                onChange={(e) => {
                                    const updatedContent = [...whyUsContent];
                                    updatedContent[index].description = e.target.value;
                                    setWhyUsContent(updatedContent);
                                }}
                            />
                        </div>
                        <div className="HomeConfigEditor-content-row">
                            <label className="HomeConfigEditor-label">Category Image:</label>
                            <div className="HomeConfigEditor-file-container">
                                {content.categoryUrl && (
                                    <div className="HomeConfigEditor-video-preview">
                                        <img
                                            src={content.categoryUrl}
                                            alt={content.category}
                                            width="200"
                                        />
                                        <button
                                            className="HomeConfigEditor-button HomeConfigEditor-delete-button"
                                            onClick={() => {
                                                deleteFile(content.categoryUrl);
                                                const updatedContent = [...whyUsContent];
                                                updatedContent[index].categoryUrl = "";
                                                setWhyUsContent(updatedContent);
                                            }}
                                        >
                                            Delete Image
                                        </button>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    accept="image/*"
                                    className="HomeConfigEditor-input-file"
                                    onChange={(e) => handleImageFileChange(index, e)}
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <button
                className="HomeConfigEditor-button HomeConfigEditor-save-button"
                onClick={handleSaveConfig}
            >
                Save Config
            </button>
        </div>
    );
};

export default HomeConfigEditor;
